/**
 * Schémas TypeBox du flow d'import self-serve v1 → v2 (cf. docs/migration-v1-to-v2-plan.md).
 *
 * Les controllers backend valident leur input avec ces schémas via le décorateur
 * `@TypeBoxBody()`. Les types frontend sont auto-générés par Orval depuis le
 * Swagger.
 */

import { Type, type Static } from '@sinclair/typebox';

/** Blocs d'import migrables. Doit rester aligné avec `MigrationBlock`
 * (`packages/backend/src/app/migration_v1/mappers/types.ts`).
 *
 * `gift_cards_payments` (cartes cadeau + paiements clients B2C) remplace
 * l'ancien bloc `subscription` : la partie abonnement / moyen de paiement /
 * remise à vie est SORTIE du moteur d'import — elle est gérée par la page
 * admin `subscription-migration` (sinon doublon segment/promo).
 * (cf. docs/to_implement/migration-beta-to-final-cutover-plan.md §6bis) */
export const MIGRATION_V1_BLOCKS = [
  'profile',
  'website',
  'catalog',
  'customers',
  'appointments',
  'gift_cards_payments',
  'marketing',
] as const;
export type MigrationV1Block = (typeof MIGRATION_V1_BLOCKS)[number];

export const MigrationV1BlockSchema = Type.Union([
  Type.Literal('profile'),
  Type.Literal('website'),
  Type.Literal('catalog'),
  Type.Literal('customers'),
  Type.Literal('appointments'),
  Type.Literal('gift_cards_payments'),
  Type.Literal('marketing'),
]);

// ─── POST /app/migration-v1/login ──────────────────────────────────────────

export const MigrationV1LoginBodySchema = Type.Object({
  email: Type.String({ format: 'email', placeholder: 'V1_LOGIN_EMAIL_PLACEHOLDER' }),
  password: Type.String({ meta: { input_type: 'password' }, placeholder: 'V1_LOGIN_PASSWORD_PLACEHOLDER' }),
});
export type MigrationV1LoginBody = Static<typeof MigrationV1LoginBodySchema>;

export const MigrationV1LoginResponseSchema = Type.Object({
  preview_ready: Type.Boolean(),
});
export type MigrationV1LoginResponse = Static<typeof MigrationV1LoginResponseSchema>;

// ─── GET /app/migration-v1/preview ─────────────────────────────────────────

/** État d'un bloc côté preview : compteurs estimés + statut d'import. */
export const MigrationV1BlockPreviewSchema = Type.Object({
  count: Type.Number({ description: "Nombre estimé d'éléments à importer pour ce bloc" }),
  status: Type.Union([Type.Literal('NOT_IMPORTED'), Type.Literal('IMPORTED'), Type.Literal('REIMPORTABLE')]),
  imported_at_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'imported_at' } })),
  warning_message: Type.Optional(Type.String()),
});
export type MigrationV1BlockPreview = Static<typeof MigrationV1BlockPreviewSchema>;

export const MigrationV1PreviewResponseSchema = Type.Object({
  blocks: Type.Object({
    profile: MigrationV1BlockPreviewSchema,
    website: MigrationV1BlockPreviewSchema,
    catalog: MigrationV1BlockPreviewSchema,
    customers: MigrationV1BlockPreviewSchema,
    appointments: MigrationV1BlockPreviewSchema,
    gift_cards_payments: MigrationV1BlockPreviewSchema,
    marketing: MigrationV1BlockPreviewSchema,
  }),
});
export type MigrationV1PreviewResponse = Static<typeof MigrationV1PreviewResponseSchema>;

// ─── POST /app/migration-v1/import ─────────────────────────────────────────

export const MigrationV1ImportBodySchema = Type.Object({
  /** Blocs sélectionnés par le pro. Profile/Catalog/Customers/Website/Appointments
   * sont présélectionnés côté UI ; tous restent décochables. L'abonnement /
   * moyen de paiement n'est PAS migré par le moteur (page admin
   * subscription-migration) ; GMB a été retiré du pipeline. */
  blocks: Type.Array(MigrationV1BlockSchema, { minItems: 1 }),
});
export type MigrationV1ImportBody = Static<typeof MigrationV1ImportBodySchema>;

export const MigrationV1ImportResponseSchema = Type.Object({
  accepted: Type.Boolean(),
  run_id: Type.String(),
});
export type MigrationV1ImportResponse = Static<typeof MigrationV1ImportResponseSchema>;

// ─── GET /app/migration-v1/status ──────────────────────────────────────────

export const MIGRATION_V1_BLOCK_STATES = ['queued', 'running', 'done', 'error', 'skipped'] as const;
export type MigrationV1BlockState = (typeof MIGRATION_V1_BLOCK_STATES)[number];

export const MigrationV1BlockStatusSchema = Type.Object({
  state: Type.Union([
    Type.Literal('queued'),
    Type.Literal('running'),
    Type.Literal('done'),
    Type.Literal('error'),
    Type.Literal('skipped'),
  ]),
  progress: Type.Number({ minimum: 0, maximum: 100 }),
  imported_count: Type.Number(),
  message: Type.Optional(Type.String()),
});
export type MigrationV1BlockStatus = Static<typeof MigrationV1BlockStatusSchema>;

export const MigrationV1StatusResponseSchema = Type.Object({
  run_id: Type.Union([Type.String(), Type.Null()]),
  is_running: Type.Boolean(),
  started_at_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'started_at' } })),
  blocks: Type.Object({
    profile: MigrationV1BlockStatusSchema,
    website: MigrationV1BlockStatusSchema,
    catalog: MigrationV1BlockStatusSchema,
    customers: MigrationV1BlockStatusSchema,
    appointments: MigrationV1BlockStatusSchema,
    gift_cards_payments: MigrationV1BlockStatusSchema,
    marketing: MigrationV1BlockStatusSchema,
  }),
});
export type MigrationV1StatusResponse = Static<typeof MigrationV1StatusResponseSchema>;

// ─── POST /app/migration-v1/reimport-block ────────────────────────────────

export const MigrationV1ReimportBlockBodySchema = Type.Object({
  block: MigrationV1BlockSchema,
});
export type MigrationV1ReimportBlockBody = Static<typeof MigrationV1ReimportBlockBodySchema>;

export const MigrationV1ReimportBlockResponseSchema = Type.Object({
  accepted: Type.Boolean(),
  run_id: Type.String(),
});
export type MigrationV1ReimportBlockResponse = Static<typeof MigrationV1ReimportBlockResponseSchema>;

// ─── POST /app/migration-v1/complete ───────────────────────────────────────

export const MigrationV1CompleteResponseSchema = Type.Object({
  completed_at_formatted: Type.String({ format: 'date-time', meta: { source_key: 'completed_at' } }),
});
export type MigrationV1CompleteResponse = Static<typeof MigrationV1CompleteResponseSchema>;
